import { blogs as seedBlogs } from "../pages/Blogs/blogsData";
import { API_BASE_URL } from "../config/env.js";
import { API_ROUTES } from "../api/routes";
import {
  DEFAULT_SECTION_STYLES,
  estimateReadTime,
  formatBlogDate,
  formatReadTime,
  getSecondaryKeywords,
  parseTags,
  slugify,
} from "../utils/blogHelpers";

const STORAGE_KEY = "arabella_admin_blogs";
const DELETED_KEY = "arabella_deleted_blog_ids";

const normalizeBlock = (block = {}) => {
  if (block.type === "video") {
    return {
      type: "video",
      src: block.src || "",
      url: block.url || "",
      caption: block.caption || "",
    };
  }

  if (block.type === "embed") {
    return {
      type: "embed",
      url: block.url || "",
      label: block.label || "",
    };
  }

  if (block.type === "image" || (block.src && !block.heading && !block.paragraphs)) {
    return {
      type: "image",
      src: block.src || "",
      alt: block.alt || "",
      caption: block.caption || "",
    };
  }

  return {
    type: "section",
    heading: block.heading || "",
    paragraphs: Array.isArray(block.paragraphs) ? block.paragraphs : [],
    headingFontFamily: block.headingFontFamily || DEFAULT_SECTION_STYLES.headingFontFamily,
    headingFontSize: block.headingFontSize || DEFAULT_SECTION_STYLES.headingFontSize,
    headingFontStyle: block.headingFontStyle || DEFAULT_SECTION_STYLES.headingFontStyle,
    contentFontFamily: block.contentFontFamily || DEFAULT_SECTION_STYLES.contentFontFamily,
    contentFontSize: block.contentFontSize || DEFAULT_SECTION_STYLES.contentFontSize,
    contentFontStyle: block.contentFontStyle || DEFAULT_SECTION_STYLES.contentFontStyle,
    image: block.image || "",
    imageAlt: block.imageAlt || "",
    video: block.video || "",
    videoUrl: block.videoUrl || "",
    videoCaption: block.videoCaption || "",
    sourceUrl: block.sourceUrl || "",
    sourceLabel: block.sourceLabel || "",
  };
};

const normalizeBlog = (blog = {}, source = "static") => ({
  id: blog.id,
  slug: blog.slug || slugify(blog.title || ""),
  title: blog.title || "",
  category: blog.category || "Travel Tips",
  date: blog.date || formatBlogDate(),
  readTime: formatReadTime(blog.readTime) || estimateReadTime(blog.content),
  primaryKeyword: blog.primaryKeyword || "",
  secondaryKeywords: getSecondaryKeywords(blog),
  secondaryKeyword: getSecondaryKeywords(blog).join(", "),
  tags: parseTags(blog.tags),
  image: blog.image || blog.bannerImage || "",
  bannerImage: blog.bannerImage || blog.image || "",
  featured: Boolean(blog.featured),
  published: blog.published !== false,
  shortDescription: blog.shortDescription || "",
  content: Array.isArray(blog.content) ? blog.content.map(normalizeBlock) : [],
  source: blog.source || source,
  createdAt: blog.createdAt || null,
  updatedAt: blog.updatedAt || null,
});

const readJson = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const writeJson = (key, value) => {
  localStorage.setItem(key, JSON.stringify(value));
};

const getLocalBlogs = () =>
  readJson(STORAGE_KEY, []).map((blog) => normalizeBlog(blog, "admin"));

const getDeletedIds = () => new Set(readJson(DELETED_KEY, []).map(String));

const saveLocalBlogs = (blogs) => writeJson(STORAGE_KEY, blogs);

const extractList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.blogs)) return payload.blogs;
  if (Array.isArray(payload?.data?.blogs)) return payload.data.blogs;
  return null;
};

const tryRemoteList = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}${API_ROUTES.BLOGS.LIST}`);
    if (!response.ok) return null;
    return extractList(await response.json());
  } catch {
    return null;
  }
};

const mergeBlogs = (remoteBlogs) => {
  const localBlogs = getLocalBlogs();
  const deletedIds = getDeletedIds();
  const localBySlug = new Map(localBlogs.map((blog) => [blog.slug, blog]));
  const remote = (remoteBlogs || []).map((blog) => normalizeBlog(blog, "api"));
  const seeds = seedBlogs.map((blog) => normalizeBlog(blog, "static"));

  const merged = [];
  const seenSlugs = new Set();

  [...localBlogs, ...remote, ...seeds].forEach((blog) => {
    if (!blog.slug || seenSlugs.has(blog.slug) || deletedIds.has(String(blog.id))) {
      return;
    }

    const override = localBySlug.get(blog.slug);
    merged.push(override || blog);
    seenSlugs.add(blog.slug);
  });

  return merged;
};

const persistAndMerge = (updater) => {
  const nextLocal = updater(getLocalBlogs());
  saveLocalBlogs(nextLocal);
  return mergeBlogs(null);
};

export const blogService = {
  getBlogs: async ({ includeDrafts = false } = {}) => {
    const remote = await tryRemoteList();
    const blogs = mergeBlogs(remote);
    return includeDrafts ? blogs : blogs.filter((blog) => blog.published);
  },

  getBlogBySlug: async (slug, { includeDrafts = false } = {}) => {
    const blogs = await blogService.getBlogs({ includeDrafts });
    return blogs.find((blog) => blog.slug === slug) || null;
  },

  getBlogById: async (id) => {
    const blogs = await blogService.getBlogs({ includeDrafts: true });
    return blogs.find((blog) => String(blog.id) === String(id)) || null;
  },

  saveBlog: async (input) => {
    const isNew = !input.id;
    const blog = normalizeBlog(
      {
        ...input,
        id: input.id || `admin-${Date.now()}`,
        slug: slugify(input.slug || input.title),
        date: input.date || formatBlogDate(),
        readTime: formatReadTime(input.readTime) || estimateReadTime(input.content),
        primaryKeyword: input.primaryKeyword || "",
        secondaryKeywords: getSecondaryKeywords(input),
        tags: parseTags(input.tags),
        createdAt: input.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        source: "admin",
      },
      "admin"
    );

    const blogs = persistAndMerge((current) => {
      const withoutCurrent = current
        .filter((item) => String(item.id) !== String(blog.id) && item.slug !== blog.slug)
        .map((item) => (blog.featured ? { ...item, featured: false } : item));

      return [blog, ...withoutCurrent];
    });

    try {
      await fetch(`${API_BASE_URL}${isNew ? API_ROUTES.BLOGS.CREATE : API_ROUTES.BLOGS.UPDATE(blog.id)}`, {
        method: isNew ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(blog),
      });
    } catch (error) {
      console.warn("Blog API save skipped, stored locally instead.", error);
    }

    return blogs.find((item) => item.slug === blog.slug);
  },

  deleteBlog: async (id) => {
    const deleted = getDeletedIds();
    deleted.add(String(id));
    writeJson(DELETED_KEY, Array.from(deleted));

    persistAndMerge((current) =>
      current.filter((blog) => String(blog.id) !== String(id))
    );

    try {
      await fetch(`${API_BASE_URL}${API_ROUTES.BLOGS.DELETE(id)}`, {
        method: "DELETE",
      });
    } catch (error) {
      console.warn("Blog API delete skipped, removed locally instead.", error);
    }
  },
};
